import FormHeader from "@/components/FormHeader";
import InputError from "@/components/InputError";
import Modal from "@/components/Modal";
import SkeletonTable from "@/components/SkeletonTable";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import useDataPendaftaran from "@/hooks/useDataPendaftaran";
import useDataStep from "@/hooks/useDataStep";
import useSessionStorage from "@/hooks/useSessionStorage";
import useTimePeriode from "@/hooks/useTimePeriode";
import api from "@/lib/axios";
import { getDataStep, getListPrestasi } from "@/services/getData";
import { DataPrestasi } from "@/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import {
  AlertTriangle,
  Award,
  CheckCircle2,
  ChevronRight,
  Edit,
  ExternalLink,
  Eye,
  FileText,
  Info,
  Loader2,
  Medal,
  Plus,
  Save,
  Trash2,
  Trophy,
} from "lucide-react";
import { useState } from "react";
import toast from "react-hot-toast";
import Form from "./partial/Form";

const storeDeskripsi = async ({
  id_data_pendaftaran,
  deskripsi,
}: {
  id_data_pendaftaran: string;
  deskripsi: string;
}) => {
  const response = await api.post(
    `/siswa/penerimaan/smbjm/prestasi/store-deskripsi`,
    {
      id_data_pendaftaran,
      deskripsi,
    },
  );
  return response.data;
};

const skipStepPrestasi = async (id_data_pendaftaran: string) => {
  const response = await api.get(
    `/siswa/penerimaan/smbjm/prestasi/skip?id_data_pendaftaran=${id_data_pendaftaran}`,
  );
  return response.data;
};

export default function Prestasi({
  idDataPendaftaran,
}: {
  idDataPendaftaran: string;
}) {
  const [sessionPeriode] = useSessionStorage("sessionPeriode", {
    id_sys_periode: "",
  });

  const { dataPendaftaran } = useDataPendaftaran();
  const { isSudahFinalisasi, isValidasiPendaftaran } = useDataStep();
  const { timePassedActive } = useTimePeriode();

  const {
    data: listDataPrestasi,
    isPending: isPendingListDataPrestasi,
    isFetched: isFetchedListDataPrestasi,
  } = useQuery({
    queryKey: ["listPrestasi"],
    queryFn: () => getListPrestasi(sessionPeriode?.id_sys_periode || ""),
  });

  const { isPending: isPendingDataStep, data: dataStep } = useQuery({
    queryKey: ["dataStep", sessionPeriode?.id_sys_periode],
    queryFn: () => getDataStep(sessionPeriode?.id_sys_periode || ""),
  });

  const [showDetail, setShowDetail] = useState(false);
  const [showInsert, setShowInsert] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showSkipPrestasi, setShowSkipPrestasi] = useState(false);
  const [selectedRow, setSelectedRow] = useState<DataPrestasi | null>(null);
  const [isPunyaPrestasi, setIsPunyaPrestasi] = useState<boolean | null>(null);

  const [form, setForm] = useState({
    deskripsi: dataPendaftaran?.bakat_talent || "",
  });

  const queryClient = useQueryClient();

  const {
    mutate: mutateDeskripsi,
    isPending: isPendingDeskripsi,
    error,
  } = useMutation({
    mutationFn: storeDeskripsi,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["dataStep"] });
      toast.success(data?.message || "Deskripsi berhasil disimpan.");
    },
    onError: (
      error: AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>,
    ) => {
      const err = error as AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>;
      toast.error(err.response?.data?.message || "Gagal menyimpan data.");
    },
  });

  const { mutate: mutateSkipPrestasi, isPending: isPendingSkip } = useMutation({
    mutationFn: skipStepPrestasi,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["dataStep"] });
      toast.success(data?.message || "Berhasil melewati tahap prestasi.");
      setShowSkipPrestasi(false);
    },
    onError: (
      error: AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>,
    ) => {
      const err = error as AxiosError<{ message?: string }>;
      toast.error(err.response?.data?.message || "Terjadi kesalahan.");
    },
  });

  const handleStoreDeskripsi = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    mutateDeskripsi({
      id_data_pendaftaran: idDataPendaftaran,
      deskripsi: form.deskripsi,
    });
  };

  const handleSkipPrestasi = () => mutateSkipPrestasi(idDataPendaftaran);

  if (isPendingListDataPrestasi || isPendingDataStep) return <SkeletonTable />;

  const isDisableInput =
    isSudahFinalisasi ||
    isValidasiPendaftaran ||
    timePassedActive?.waktu_selesai_daftar_valid_selesai;

  // Render Component: Empty State for Prestasi List
  const EmptyPrestasiState = () => (
    <div className="flex flex-col items-center justify-center p-8 border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-xl bg-slate-50/50 dark:bg-slate-900/20 text-center">
      <div className="p-4 bg-white dark:bg-slate-900 rounded-full shadow-sm mb-4">
        <Trophy className="w-10 h-10 text-slate-300 dark:text-slate-600" />
      </div>
      <h3 className="text-lg font-bold text-slate-900 dark:text-white">
        Tidak Ada Data Prestasi
      </h3>
      <p className="text-slate-500 text-sm max-w-sm mb-6">
        Anda memilih tidak memiliki prestasi. Anda tetap bisa mengisi deskripsi
        bakat di bawah.
      </p>
    </div>
  );

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      {/* HEADER */}
      <div className="flex items-center gap-3 border-b border-slate-200 dark:border-slate-800 pb-4">
        <Medal className="w-6 h-6 text-blue-600 dark:text-blue-400" />
        <div>
          <h1 className="text-xl font-bold text-slate-900 dark:text-white">
            Prestasi & Bakat
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Input data prestasi dan deskripsi bakat unggulan.
          </p>
        </div>
      </div>

      {/* --- SECTION 1: PILIHAN PUNYA PRESTASI (Kondisional) --- */}
      {isFetchedListDataPrestasi &&
        listDataPrestasi?.length === 0 &&
        !isDisableInput &&
        dataStep?.data_prestasi !== 1 && (
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6 shadow-sm">
            <h3 className="font-semibold text-lg mb-4">
              Apakah Anda memiliki prestasi?
            </h3>
            <div className="flex flex-col sm:flex-row gap-4 w-full">
              <div
                className={`flex-1 p-4 border rounded-xl cursor-pointer flex flex-col items-center gap-2 transition-all group ${
                  isPunyaPrestasi === true
                    ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20 ring-1 ring-blue-500"
                    : "hover:border-blue-500 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800"
                }`}
                onClick={() => {
                  setIsPunyaPrestasi(true);
                  setShowInsert(true);
                }}
              >
                <div className="p-2 bg-blue-50 dark:bg-blue-900/30 rounded-full text-blue-600 group-hover:scale-110 transition-transform">
                  <Plus className="w-5 h-5" />
                </div>
                <span className="font-medium text-sm">Ya, Saya Punya</span>
              </div>
              <div
                className={`flex-1 p-4 border rounded-xl cursor-pointer flex flex-col items-center gap-2 transition-all group ${
                  isPunyaPrestasi === false
                    ? "border-slate-500 bg-slate-50 dark:bg-slate-800"
                    : "hover:border-slate-400 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800"
                }`}
                onClick={() => setIsPunyaPrestasi(false)}
              >
                <div className="p-2 bg-slate-100 dark:bg-slate-800 rounded-full text-slate-600 group-hover:scale-110 transition-transform">
                  <ChevronRight className="w-5 h-5" />
                </div>
                <span className="font-medium text-sm text-slate-600 dark:text-slate-400">
                  Tidak Ada
                </span>
              </div>
            </div>
          </div>
        )}

      {/* --- SECTION 2: LIST PRESTASI (Jika Punya / Sudah Ada Data) --- */}
      {(isPunyaPrestasi === true ||
        (listDataPrestasi && listDataPrestasi.length > 0)) && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <Award className="w-5 h-5 text-amber-500" /> Data Prestasi
            </h2>
          </div>

          <Alert className="bg-amber-50 border-amber-200 text-amber-900 dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-200">
            <Info className="h-4 w-4" />
            <AlertTitle className="font-bold">Ketentuan</AlertTitle>
            <AlertDescription className="text-xs">
              Maksimal 3 data prestasi terbaik. File bukti harus berformat
              PDF/Gambar yang jelas.
            </AlertDescription>
          </Alert>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {/* Tombol Tambah (Card Dashed) - Pindah ke atas agar konsisten UX-nya */}
            {!isDisableInput &&
              listDataPrestasi &&
              listDataPrestasi.length < 3 && (
                <button
                  onClick={() => setShowInsert(true)}
                  className="flex flex-col items-center justify-center gap-3 p-6 border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-xl hover:border-blue-500 hover:bg-blue-50/50 dark:hover:bg-blue-900/10 transition-all group min-h-[200px]"
                >
                  <div className="p-3 bg-slate-100 dark:bg-slate-800 rounded-full text-slate-400 group-hover:bg-blue-100 group-hover:text-blue-600 transition-colors">
                    <Plus className="w-6 h-6" />
                  </div>
                  <span className="font-semibold text-slate-500 group-hover:text-blue-600 text-sm">
                    Tambah Prestasi
                  </span>
                </button>
              )}

            {/* Card List Prestasi */}
            {listDataPrestasi?.map((item: DataPrestasi, index: number) => (
              <div
                key={item.id_data_prestasi}
                className="relative flex flex-col bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-sm overflow-hidden group hover:shadow-md transition-shadow"
              >
                {/* Card Header */}
                <div className="p-4 border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 flex justify-between items-start">
                  <div className="flex items-center gap-2">
                    <Badge
                      variant="secondary"
                      className="bg-white border-slate-200 text-slate-700 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-300"
                    >
                      #{index + 1}
                    </Badge>
                    <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-900/30">
                      {item.jenis_prestasi?.nama_jenis_prestasi}
                    </Badge>
                  </div>

                  {/* Inline Action Buttons (Edit & Delete) */}
                  {!isDisableInput && (
                    <div className="flex items-center gap-1 -mr-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-slate-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:text-blue-400 dark:hover:bg-blue-900/20"
                        title="Edit Prestasi"
                        onClick={() => {
                          setShowEdit(true);
                          setSelectedRow(item);
                        }}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:text-red-400 dark:hover:bg-red-900/20"
                        title="Hapus Prestasi"
                        onClick={() => {
                          setShowDelete(true);
                          setSelectedRow(item);
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>

                {/* Card Body */}
                <div className="p-4 space-y-3 text-sm flex-1">
                  <div className="space-y-1">
                    <p className="text-xs text-slate-500 uppercase font-semibold tracking-wider">
                      Tingkat & Capaian
                    </p>
                    <p className="font-medium text-slate-800 dark:text-slate-200 line-clamp-2">
                      {item.jenjang_prestasi?.nama_jenjang_prestasi} —{" "}
                      {item.capaian_prestasi?.nama_capaian_prestasi}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-slate-500 uppercase font-semibold tracking-wider">
                      Tahun
                    </p>
                    <p className="font-medium text-slate-800 dark:text-slate-200">
                      {item.tahun_prestasi}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-slate-500 uppercase font-semibold tracking-wider">
                      Penyelenggara
                    </p>
                    <p className="font-medium text-slate-800 dark:text-slate-200 line-clamp-1">
                      {item.lembaga_pemberi_prestasi}
                    </p>
                  </div>

                  {/* UI/UX Bukti Prestasi (Interactive Link) */}
                  {item.path_bukti_prestasi && (
                    <div className="mt-3 pt-2">
                      <a
                        href={item.path_bukti_prestasi}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-100 rounded-lg hover:bg-blue-100 hover:text-blue-700 dark:text-blue-400 dark:bg-blue-900/20 dark:border-blue-800 dark:hover:bg-blue-900/40 transition-all w-fit group/link"
                      >
                        <FileText className="w-3.5 h-3.5" />
                        Lihat Bukti
                        <ExternalLink className="w-3 h-3 ml-0.5 opacity-50 group-hover/link:opacity-100 transition-opacity" />
                      </a>
                    </div>
                  )}
                </div>

                {/* Card Footer (Actions: Hanya Detail) */}
                <div className="p-3 border-t border-slate-100 dark:border-slate-800 bg-slate-50/30 dark:bg-slate-900/30">
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full text-xs h-8 bg-white dark:bg-slate-900"
                    onClick={() => {
                      setShowDetail(true);
                      setSelectedRow(item);
                    }}
                  >
                    <Eye className="w-3.5 h-3.5 mr-1.5" /> Detail Lengkap
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* TAMPILKAN EMPTY STATE JIKA USER MEMILIH TIDAK PUNYA PRESTASI */}
      {isPunyaPrestasi === false && <EmptyPrestasiState />}

      <Separator className="my-8" />

      {/* --- SECTION 3: DESKRIPSI BAKAT --- */}

      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6 shadow-sm">
        <h3 className="font-bold text-lg text-slate-900 dark:text-white mb-4 flex items-center gap-2">
          <Info className="w-5 h-5 text-blue-600" /> Deskripsi Bakat &
          Keterampilan
        </h3>

        <Alert className="bg-blue-50 border-blue-200 text-blue-900 mb-6 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-200">
          <Info className="h-4 w-4" />
          <AlertTitle className="font-bold">Informasi</AlertTitle>
          <AlertDescription className="text-xs">
            Jelaskan secara rinci bakat, keterampilan, atau prestasi lain yang
            menjadi keunggulan Anda namun tidak memiliki sertifikat resmi.
          </AlertDescription>
        </Alert>

        <form onSubmit={handleStoreDeskripsi} className="space-y-4">
          <div className="space-y-2">
            <Label required>Deskripsi Diri</Label>
            <Textarea
              minLength={100}
              className={`min-h-[150px] resize-y transition-colors ${
                form.deskripsi.length > 0 && form.deskripsi.length < 100
                  ? "border-amber-300 focus-visible:ring-amber-500 dark:border-amber-700/50"
                  : form.deskripsi.length >= 100
                    ? "border-emerald-300 focus-visible:ring-emerald-500 dark:border-emerald-700/50"
                    : ""
              }`}
              placeholder="Contoh: Saya memiliki kemampuan kepemimpinan yang baik karena aktif sebagai Ketua OSIS..."
              value={form.deskripsi}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, deskripsi: e.target.value }))
              }
              disabled={isDisableInput}
            />

            {/* --- UI/UX PROGRESS CHARACTER --- */}
            <div className="flex flex-col gap-2 pt-1">
              <div className="flex justify-between items-end text-xs">
                <span
                  className={`flex items-center gap-1 font-medium transition-colors ${
                    form.deskripsi.length === 0
                      ? "text-slate-500"
                      : form.deskripsi.length < 100
                        ? "text-amber-600 dark:text-amber-500"
                        : "text-emerald-600 dark:text-emerald-400"
                  }`}
                >
                  {form.deskripsi.length === 0 ? (
                    "Minimal 100 karakter."
                  ) : form.deskripsi.length < 100 ? (
                    `Kurang ${100 - form.deskripsi.length} karakter lagi.`
                  ) : (
                    <>
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      Batas minimal terpenuhi.
                    </>
                  )}
                </span>
                <span
                  className={`font-bold font-mono tracking-tight ${
                    form.deskripsi.length < 100
                      ? "text-slate-500"
                      : "text-emerald-600 dark:text-emerald-400"
                  }`}
                >
                  {form.deskripsi.length}{" "}
                  <span className="font-normal text-slate-400">/ Min. 100</span>
                </span>
              </div>

              {/* Progress Bar Line */}
              <div className="h-1.5 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-300 ease-out ${
                    form.deskripsi.length === 0
                      ? "w-0 bg-transparent"
                      : form.deskripsi.length < 50
                        ? "bg-red-500"
                        : form.deskripsi.length < 100
                          ? "bg-amber-500"
                          : "bg-emerald-500"
                  }`}
                  style={{
                    width: `${Math.min((form.deskripsi.length / 100) * 100, 100)}%`,
                  }}
                />
              </div>
            </div>
            {/* ---------------------------------- */}

            <InputError
              text={error?.response?.data?.errors?.deskripsi?.join(",")}
            />
          </div>

          {!isDisableInput && (
            <div className="flex justify-end pt-3">
              <Button
                type="submit"
                // Tombol disable jika API sedang loading ATAU karakter kurang dari 100
                disabled={isPendingDeskripsi || form.deskripsi.length < 100}
                className="bg-blue-600 hover:bg-blue-700 text-white min-w-[150px] transition-all"
              >
                {isPendingDeskripsi ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" /> Simpan Deskripsi
                  </>
                )}
              </Button>
            </div>
          )}
        </form>
      </div>

      {/* --- MODALS --- */}
      {/* Modal Insert */}
      <Modal
        show={showInsert}
        onClose={() => setShowInsert(false)}
        className="lg:w-[48vw]"
      >
        <Form
          method="POST"
          closeModal={() => setShowInsert(false)}
          idDataPendaftaran={idDataPendaftaran}
        />
      </Modal>

      {/* Modal Edit */}
      <Modal
        show={showEdit}
        onClose={() => setShowEdit(false)}
        className="lg:w-[48vw]"
      >
        <Form
          method="PATCH"
          closeModal={() => setShowEdit(false)}
          selectedRow={selectedRow}
          idDataPendaftaran={idDataPendaftaran}
        />
      </Modal>

      {/* Modal Delete */}
      <Modal show={showDelete} onClose={() => setShowDelete(false)}>
        <Form
          method="DELETE"
          closeModal={() => setShowDelete(false)}
          selectedRow={selectedRow}
        />
      </Modal>

      {/* Modal Detail */}
      <Modal
        show={showDetail}
        onClose={() => setShowDetail(false)}
        className="md:w-[35rem]"
      >
        <FormHeader title="Detail Prestasi" icon={<Award />} />
        <div className="p-4 space-y-4">
          {selectedRow && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-3 gap-2 border-b pb-2">
                <span className="text-slate-500">Jenis</span>
                <span className="col-span-2 font-medium">
                  {selectedRow.jenis_prestasi?.nama_jenis_prestasi}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2 border-b pb-2">
                <span className="text-slate-500">Tingkat</span>
                <span className="col-span-2 font-medium">
                  {selectedRow.jenjang_prestasi?.nama_jenjang_prestasi}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2 border-b pb-2">
                <span className="text-slate-500">Capaian</span>
                <span className="col-span-2 font-medium">
                  {selectedRow.capaian_prestasi?.nama_capaian_prestasi}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2 border-b pb-2">
                <span className="text-slate-500">Tahun</span>
                <span className="col-span-2 font-medium">
                  {selectedRow.tahun_prestasi}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <span className="text-slate-500">Penyelenggara</span>
                <span className="col-span-2 font-medium">
                  {selectedRow.lembaga_pemberi_prestasi}
                </span>
              </div>
            </div>
          )}
          <div className="pt-4 text-right">
            <Button variant="outline" onClick={() => setShowDetail(false)}>
              Tutup
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal Skip Confirmation */}
      <Modal show={showSkipPrestasi} onClose={() => setShowSkipPrestasi(false)}>
        <div className="p-6 text-center">
          <div className="mx-auto w-12 h-12 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center mb-4">
            <AlertTriangle className="w-6 h-6" />
          </div>
          <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">
            Konfirmasi Tidak Ada Prestasi
          </h3>
          <p className="text-slate-500 text-sm mb-6">
            Apakah Anda yakin tidak memiliki riwayat prestasi? <br />
            Anda akan melewatkan kesempatan menambah poin seleksi dari jalur
            prestasi.
          </p>
          <div className="flex gap-3 justify-center">
            <Button
              variant="outline"
              onClick={() => setShowSkipPrestasi(false)}
              disabled={isPendingSkip}
            >
              Batal
            </Button>
            <Button
              onClick={handleSkipPrestasi}
              disabled={isPendingSkip}
              className="bg-amber-600 hover:bg-amber-700 text-white hover:text-white"
            >
              {isPendingSkip ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                "Ya, Saya Yakin"
              )}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
